import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReportIssueButton from "@/components/ReportIssueButton";
// Landing is eager — it is the LCP page for anonymous visitors, and
// code-splitting it would net a *worse* result because we would delay
// the very first meaningful paint by one extra network round-trip.
import Landing from "@/pages/Landing";

// Auth flows — grouped in one lazy chunk since they follow each other.
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Forgot = lazy(() => import("@/pages/Forgot"));
const Reset = lazy(() => import("@/pages/Reset"));

// Browse / detail — moderately heavy pages
const BrowseUpcoming = lazy(() => import("@/pages/BrowseUpcoming"));
const BrowseRecorded = lazy(() => import("@/pages/BrowseRecorded"));
const LectureDetail = lazy(() => import("@/pages/LectureDetail"));
const WatchRecorded = lazy(() => import("@/pages/WatchRecorded"));
const PublicProfile = lazy(() => import("@/pages/PublicProfile"));
const Campus = lazy(() => import("@/pages/Campus"));
const Converse = lazy(() => import("@/pages/Converse"));
const Contact = lazy(() => import("@/pages/Contact"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const SpeakerInvite = lazy(() => import("@/pages/SpeakerInvite"));

// Authed / heavy pages — only load when a signed-in user hits them.
// Dashboard imports Recharts (~120 KB), AdminPanel is very large,
// LiveRoom pulls Jitsi hooks. These were the biggest drags on LCP.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CreateLecture = lazy(() => import("@/pages/CreateLecture"));
const LiveRoom = lazy(() => import("@/pages/LiveRoom"));
const Profile = lazy(() => import("@/pages/Profile"));
const Schedule = lazy(() => import("@/pages/Schedule"));
const Messages = lazy(() => import("@/pages/Messages"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center text-slate-400 text-sm">Loading…</div>
);

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <RouteFallback />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Navbar />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot" element={<Forgot />} />
              <Route path="/reset" element={<Reset />} />
              <Route path="/upcoming" element={<BrowseUpcoming />} />
              <Route path="/lectures" element={<Navigate to="/upcoming" replace />} />
              <Route path="/recorded" element={<BrowseRecorded />} />
              <Route path="/lectures/:id" element={<LectureDetail />} />
              <Route path="/lectures/:id/live" element={<Protected><LiveRoom /></Protected>} />
              <Route path="/lectures/:id/watch" element={<WatchRecorded />} />
              <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
              <Route path="/create" element={<Protected><CreateLecture /></Protected>} />
              <Route path="/profile" element={<Protected><Profile /></Protected>} />
              <Route path="/u/:username" element={<PublicProfile />} />
              <Route path="/speaker/:token" element={<SpeakerInvite />} />
              <Route path="/campus" element={<Campus />} />
              <Route path="/schedule" element={<Protected><Schedule /></Protected>} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/converse" element={<Converse />} />
              <Route path="/messages" element={<Protected><Messages /></Protected>} />
              <Route path="/messages/:cid" element={<Protected><Messages /></Protected>} />
              <Route path="/admin" element={<Protected><AdminPanel /></Protected>} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
            </Routes>
          </Suspense>
          <Footer />
          <ReportIssueButton />
          <Toaster richColors closeButton position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
